import { DOCUMENT } from '@angular/common'
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { RootState, selectUserPolicy } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ToggleStates } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { UiScale } from '../../services/ui-scale'
import {
  DensityLevel,
  UserPreferencesService,
} from '../../services/user-preferences.service'
import { BulkDialogSortWarningModalService } from '../modals/bulk-dialog-sort-warning-modal/bulk-dialog-sort-warning-modal.service'

@Component({
  selector: 'hs-user-preferences-drawer',
  templateUrl: './user-preferences-drawer.component.html',
  styleUrls: ['./user-preferences-drawer.component.scss'],
})
export class UserPreferencesDrawerComponent implements OnInit, OnDestroy {
  @Output() closePanel = new EventEmitter<void>()
  @Input() isVisible = false

  private readonly destroySubject$ = new Subject<void>()
  private readonly onFullScreenChangeBinding =
    this.onFullScreenChangeHandler.bind(this)

  compactMode: FormControl = new FormControl(
    UserPreferencesService.getDensityLevel() === DensityLevel.Compact
      ? true
      : false,
  )

  // Not the static getter: it applies the zoom to the DOM as a side effect, which
  // a form-control initialiser has no business doing.
  uiScale: FormControl = new FormControl(this.preferenceService.uiScale)

  focusMode: FormControl = new FormControl(false)

  gridLines: FormControl = new FormControl(
    !this.preferenceService.areGridLinesRemoved(),
  )

  reduxDevtools: FormControl = new FormControl(
    UserPreferencesService.getReduxDevtools(),
  )

  deletePromptPreference: FormControl = new FormControl(
    UserPreferencesService.getHideDeletePromptPreference(),
  )

  forceCloseSessionPreference: FormControl = new FormControl(
    UserPreferencesService.getForceCloseSessionPreference(),
  )

  autoRefreshAccount = new FormControl(
    UserPreferencesService.getAutoRefreshAccount(),
  )

  bulkSortPreference: FormControl = new FormControl(
    UserPreferencesService.getBulkDialogSortPreference(),
  )

  useOldMultiRowGridCode: FormControl = new FormControl(
    UserPreferencesService.getUseOldGridMultiRow(),
  )

  saveModalPositions: FormControl = new FormControl(
    UserPreferencesService.getSaveModalPositions(),
  )

  developerOnly$ = this.store
    .select(selectUserPolicy)
    .pipe(
      distinctUntilChanged(
        (a, b) => a?.accessDeveloperTools === b?.accessDeveloperTools,
      ),
    )

  UiScale = UiScale

  states: ToggleStates = {
    on: 'On',
    off: 'Off',
  }

  /** @internal */
  _ignoreDocumentFullscreenChange = false
  /** @internal */
  _ignoreFullScreenActions = false

  constructor(
    private readonly preferenceService: UserPreferencesService,
    private readonly bulkDialogSortWarningService: BulkDialogSortWarningModalService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly store: Store<RootState>,
  ) {}

  ngOnInit(): void {
    this.compactMode.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this.preferenceService.setDensityLevel(
          value ? DensityLevel.Compact : DensityLevel.Comfortable,
        )
      })

    this.uiScale.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((value: UiScale) => {
        this.preferenceService.setUiScale(value)
      })

    this.focusMode.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this._ignoreDocumentFullscreenChange = true
        this.preferenceService.setFocusMode(
          value,
          this._ignoreFullScreenActions,
        )
        this._ignoreFullScreenActions = false
      })

    this.gridLines.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(shouldKeep => {
        this.preferenceService.removeGridLines(!shouldKeep)
      })

    this.reduxDevtools.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this.preferenceService.setReduxDevtools(value)
        this.document.location.reload()
      })

    this.useOldMultiRowGridCode.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this.preferenceService.setUseOldGridMultiRow(value)
        this.document.location.reload()
      })
    this.deletePromptPreference.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this._ignoreDocumentFullscreenChange = true
        this.preferenceService.setDeletePromptPreference(value)
        this._ignoreFullScreenActions = false
      })

    this.forceCloseSessionPreference.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this.preferenceService.setForceCloseSessionPreference(value)
      })

    this.autoRefreshAccount.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this._ignoreDocumentFullscreenChange = true
        this.preferenceService.setAutoRefreshAccount(value)
        this._ignoreFullScreenActions = false
      })

    this.bulkSortPreference.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(async value => {
        this._ignoreDocumentFullscreenChange = true
        if (value) {
          try {
            await this.bulkDialogSortWarningService.toggle()
            this.preferenceService.setBulkDialogSortPreference(true)
          } catch (error) {
            this.preferenceService.setBulkDialogSortPreference(false)
            this.bulkSortPreference.patchValue(false)
          }
        } else {
          this.preferenceService.setBulkDialogSortPreference(false)
        }
        this._ignoreFullScreenActions = false
      })

    this.saveModalPositions.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        this._ignoreDocumentFullscreenChange = true
        this.preferenceService.setSaveModalPositions(value)
        this._ignoreFullScreenActions = false
      })

    this.document.addEventListener(
      'fullscreenchange',
      this.onFullScreenChangeBinding,
    )
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()

    this.document.removeEventListener(
      'fullscreenchange',
      this.onFullScreenChangeBinding,
    )
  }

  private onFullScreenChangeHandler(e: Event) {
    if (this._ignoreDocumentFullscreenChange) {
      this._ignoreDocumentFullscreenChange = false
      return
    }

    const target = e.target as Document
    if (!target.fullscreenEnabled) {
      // Ignore
      this._ignoreFullScreenActions = true
      this.focusMode.setValue(false)
    }
  }
}
